import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react';

import {
    GetContractDealsByIdsInput,
    GetContractDealsByIdsOutput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';

import { isNil } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { RateCardDetail } from 'src/components/RateCardDetail';
import { ViewPrimeAMFRateCard } from 'src/components/ViewPrimeAMFRateCard';
import { AppContext } from 'src/contexts/appContext';

import { EmbeddedScrollable } from '../../EmbeddedScrollable';

interface ISideBySideViewer {
    leftId?: number;
    rightId?: number;
}

function getRenderer(contractId: number | undefined, serviceIdMap: Record<number, number>): ReactElement {
    if (isNil(contractId) || isNil(serviceIdMap[contractId])) {
        return <></>;
    } else if (serviceIdMap[contractId] === 1) {
        return <RateCardDetail rateCardId={contractId} isEditMode={false} isEditDraft={false} isEmbedded={true} />;
    } else {
        return <ViewPrimeAMFRateCard rateCardId={contractId} serviceId={serviceIdMap[contractId]} isEmbedded={true} />;
    }
}

export const SideBySideViewer: React.FC<ISideBySideViewer> = (props) => {
    const [serviceIdMap, setServiceIdMap] = useState<Record<number, number>>({});
    const { setTopAlertMsg } = useContext(AppContext);

    const leftSide = getRenderer(props.leftId, serviceIdMap);
    const rightSide = getRenderer(props.rightId, serviceIdMap);

    useEffect(() => {
        const contractIds = [];
        if (!isNil(props.leftId) && isNil(serviceIdMap[props.leftId])) {
            contractIds.push(props.leftId);
        }
        if (!isNil(props.rightId) && isNil(serviceIdMap[props.rightId])) {
            contractIds.push(props.rightId);
        }
        if (contractIds.length === 0) {
            return;
        }
        // Use GetContractDealsByIds to fetch the serviceId for the regarding contractId
        contractConfigurationClient
            .getContractDealsByIds(
                GetContractDealsByIdsInput.clone({
                    contractIds: contractIds,
                    includeDraftContracts: true,
                    includeDeletedContracts: true,
                    includeDeletedContractDeals: true,
                    includeIneligibleContractDeals: true
                })
            )
            .subscribe(
                (response: GetContractDealsByIdsOutput) => {
                    const newMappings: Record<number, number> = {};
                    response.contractDeals.forEach((deal) => (newMappings[deal.contractId] = deal.serviceId));
                    setServiceIdMap((oldMappings) => ({ ...oldMappings, ...newMappings }));
                },
                (error: UnknownCoralError) => {
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    }, [props.leftId, props.rightId]);

    return (
        <Row width={'100%'} widths={'fill'} height={'100%'}>
            <EmbeddedScrollable>{leftSide}</EmbeddedScrollable>
            <EmbeddedScrollable>{rightSide}</EmbeddedScrollable>
        </Row>
    );
};
